import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ArrowUpRight, MessageCircle } from "lucide-react";
import { PRODUCTS, PRODUCT_CATEGORIES, BRAND } from "@/data/urban";
import { InquiryForm } from "@/components/ur/InquiryForm";

const PageHead = ({ overline, h1, sub }) => (
  <div className="pt-32 pb-8 bg-canvas">
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
      <div className="overline text-ink-soft">{overline}</div>
      <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-ink leading-tight">{h1}</h1>
      {sub && <p className="mt-3 text-ink-soft max-w-2xl">{sub}</p>}
    </div>
  </div>
);

const waMsg = (p) => encodeURIComponent(`Hi Urban Renovations, I'm interested in ${p.name} (${p.code}). Please share more details.`);

export const Products = () => {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("popular");
  const cat = params.get("cat") || "all";

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  const filtered = PRODUCTS
    .filter((p) => (cat === "all" || p.category === cat))
    .filter((p) => (q ? (p.name + " " + p.code + " " + p.material).toLowerCase().includes(q.toLowerCase()) : true))
    .sort((a, b) => {
      if (sort === "low") return parseInt(a.price.replace(/[^0-9]/g, ""), 10) - parseInt(b.price.replace(/[^0-9]/g, ""), 10);
      if (sort === "high") return parseInt(b.price.replace(/[^0-9]/g, ""), 10) - parseInt(a.price.replace(/[^0-9]/g, ""), 10);
      return 0;
    });

  return (
    <>
      <PageHead overline="Product Catalog" h1="Explore our products" sub="Modular kitchens, wardrobes, doors, TV units and interior products — all designed for Indian homes." />

      <section className="pb-24 bg-canvas">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            {PRODUCT_CATEGORIES.map((c) => (
              <button key={c.key} type="button" data-testid={`product-cat-${c.key}`}
                onClick={() => setParams(c.key === "all" ? {} : { cat: c.key })}
                className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider border transition-colors ${cat === c.key ? "bg-black text-white border-black" : "bg-white text-ink border-hairline hover:border-ink"}`}>{c.label}</button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
              <input data-testid="product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products..." className="w-full h-12 pl-10 pr-4 rounded-md border border-hairline bg-white text-ink text-sm focus:outline-none focus:border-ink" />
            </div>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="product-sort" className="h-12 rounded-md border border-hairline bg-white px-4 text-sm text-ink focus:outline-none focus:border-ink">
              <option value="popular">Popular</option>
              <option value="low">Price: Low to High</option>
              <option value="high">Price: High to Low</option>
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p) => (
              <article key={p.slug} data-testid={`product-card-${p.slug}`} className="group rounded-2xl overflow-hidden bg-white border border-hairline hover:-translate-y-0.5 transition-transform">
                <div className="aspect-[4/3] overflow-hidden bg-canvas">
                  <img src={p.img} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-widest text-ink-soft">{p.code}</div>
                  <h3 className="mt-1 font-heading text-lg font-bold text-ink">{p.name}</h3>
                  <p className="mt-1.5 text-xs text-ink-soft">{p.material} · {p.finish}</p>
                  <div className="mt-3 font-heading text-lg font-extrabold text-ink">{p.price} <span className="text-xs font-normal text-ink-soft">onwards</span></div>
                  {p.category === "modular-kitchen" && (
                    <div className="mt-0.5 text-[11px] text-ink-soft">or from ₹1,200/sq.ft</div>
                  )}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <Link to={`/product/${p.slug}`} className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-ink hover:opacity-70">View Details <ArrowUpRight className="h-3.5 w-3.5" /></Link>
                    <span className="text-hairline">·</span>
                    <Link to={`/product/${p.slug}#inquiry`} className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-ink hover:opacity-70">Get Quote</Link>
                    <a href={`https://wa.me/${BRAND.whatsapp}?text=${waMsg(p)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-ink hover:opacity-70"><MessageCircle className="h-3 w-3" /> WhatsApp</a>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {filtered.length === 0 && <div className="py-16 text-center text-ink-soft">No products match your search.</div>}

          <div className="mt-16 grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <div className="overline text-ink-soft">Not sure what you need?</div>
              <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-extrabold text-ink">Talk to a designer</h2>
              <p className="mt-3 text-ink-soft">Share your requirement and we'll help you shortlist the right products, materials and finishes within your budget.</p>
            </div>
            <InquiryForm heading="Get Product Consultation" source="products-page" testId="products-inquiry" />
          </div>
        </div>
      </section>
    </>
  );
};

export const ProductDetail = () => {
  const slug = window.location.pathname.split("/").pop();
  const product = PRODUCTS.find((p) => p.slug === slug);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [slug]);

  if (!product) return (
    <div className="pt-32 pb-24 max-w-3xl mx-auto text-center px-4">
      <h1 className="font-heading text-3xl font-bold text-ink">Product not found</h1>
      <Link to="/products" className="mt-6 inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-md font-bold uppercase tracking-wider text-sm">Back to products</Link>
    </div>
  );

  const related = PRODUCTS.filter((p) => p.category === product.category && p.slug !== product.slug).slice(0, 3);

  return (
    <>
      <div className="pt-32 pb-16 bg-canvas">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <nav className="text-xs text-ink-soft mb-6"><Link to="/" className="hover:text-ink">Home</Link> / <Link to="/products" className="hover:text-ink">Products</Link> / <span className="text-ink">{product.name}</span></nav>
          <div className="grid lg:grid-cols-2 gap-10">
            <div className="rounded-2xl overflow-hidden bg-white border border-hairline">
              <img src={product.img} alt={product.name} className="w-full aspect-square object-cover" />
            </div>
            <div>
              <div className="overline text-ink-soft">{product.code}</div>
              <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-extrabold text-ink">{product.name}</h1>
              <div className="mt-3 font-heading text-3xl font-extrabold text-ink">{product.price} <span className="text-sm font-normal text-ink-soft">onwards</span></div>
              <p className="mt-5 text-ink-soft leading-relaxed">{product.desc}</p>
              <dl className="mt-6 grid grid-cols-2 gap-3">
                {[["Material", product.material], ["Finish", product.finish], ["Customizable", "Yes"], ["Warranty", "Available"]].map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-hairline p-3">
                    <dt className="text-[10px] uppercase tracking-widest text-ink-soft">{k}</dt>
                    <dd className="text-sm font-semibold text-ink mt-1">{v}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#inquiry" className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-md font-bold uppercase tracking-wider text-sm hover:bg-ink transition-colors">Get Product Quote</a>
                <a href={`https://wa.me/${BRAND.whatsapp}?text=${waMsg(product)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-hairline text-ink px-6 py-3 rounded-md font-bold uppercase tracking-wider text-sm hover:border-ink"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
                <a href={`tel:${BRAND.phoneRaw}`} className="inline-flex items-center gap-2 text-ink px-6 py-3 rounded-md font-bold uppercase tracking-wider text-sm hover:opacity-70">Call Now</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <h2 className="font-heading text-2xl font-extrabold text-ink">Related products</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
              {related.map((p) => (
                <Link key={p.slug} to={`/product/${p.slug}`} className="group rounded-2xl overflow-hidden bg-white border border-hairline">
                  <div className="aspect-[4/3] overflow-hidden"><img src={p.img} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                  <div className="p-4"><div className="font-heading font-bold text-ink text-sm">{p.name}</div><div className="text-xs text-ink-soft mt-0.5">{p.price} onwards</div></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section id="inquiry" className="py-20 bg-canvas">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10">
          <InquiryForm heading="Interested in This Product?" subheading="We'll get back within 24 hours with a detailed quote." source={`product:${product.slug}`} productName={product.name} productCode={product.code} defaultService={product.category === "modular-kitchen" ? "Modular Kitchen" : product.category === "wardrobes" ? "Wardrobes" : product.category === "doors" ? "Designer Doors" : undefined} testId="product-inquiry" />
        </div>
      </section>
    </>
  );
};
